import ENDPOINTTYPE from './EndpointType';
import { parseGraphQlEndpointData } from './graphQLEndpoint';
import { parseRestEndpointData } from './restEndpoint';
import { parseTestEndpointData } from './testEndpoint';

import hash from '../../utils/hash';
import createCache from '../../utils/createCache';

const _PROD_ = process.env.NODE_ENV === 'production';

/**
 * EndpointService creator
 *
 * @param {Joki} jokiInstance
 * @param {Object} serviceOptions - An object containing settings for the service
 * @param {string} serviceOptions.serviceId - The id for the this service, defaults to EndpointService if not defined.
 * @param {Object[]} serviceOptions.services - And array of which defines where each Endppoint Type points at
 * @param {string} serviceOptions.services[].type - The type this configuration is meant for
 * @param {string} serviceOptions.services[].requestKey - The event key that is called by this endpoint type
 * @param {string} serviceOptions.services[].serviceId - The service this endpoint type is calling
 * @param {Object[]} endpointSettings - An array of endpoints
 * @param {string} endpointSettings[].id - The unique id for this endpoint that is used to activate it.
 * @param {string} endpointSettings[].type - The type that is used with this endpoint
 * @param {string} endpointSettings[].url - What is the url that is called by this endpoint
 * @param {string} [endpointSettings[].query] - The query that is sent when using GRAPHQL endpoint type
 */
export default function createEndpointService(jokiInstance, serviceOptions = {}, endpointSettings = []) {
    const serviceId = serviceOptions.serviceId || 'EndpointService';
    const joki = jokiInstance;

    // const endpointQueue = endpointHistoryQueue({ lifetime: 30000 });
    const endpointCache = createCache({ lifetime: 100 });

    try {
        endpointValidator(endpointSettings, true);
    } catch (errs) {
        throw errs;
    }

    const endpoints = new Map();

    if (!Array.isArray(serviceOptions.services)) {
        throw new EndpointError(`${serviceId}: serviceOptions.services must be an array of objects!`);
    }

    const services = !Array.isArray(serviceOptions.services)
        ? new Map()
        : serviceOptions.services.reduce((acc, cur) => {
            acc.set(cur.type, cur);
            return acc;
        }, new Map());

    if (services.size === 0) {
        //console.warn(`\n\n${serviceId}\nNo services defined for endpoints. This will cause errors!\n\n`);
    }

    // Handling the saved settings in the global store (browser's local storage)
    localStorage.setItem("mode", "READ");
    let defaultOrganization = localStorage.getItem("selectedOrganization");
    defaultOrganization = defaultOrganization && defaultOrganization !== 'null' && defaultOrganization !== 'undefined' ? 
        { organization: JSON.stringify(defaultOrganization)} : {};
    const globalFilters = serviceOptions.globalFilters || defaultOrganization;

    endpointSettings.forEach(ep => {
        if (!_PROD_ || ep.type !== ENDPOINTTYPE.TEST) {
            endpoints.set(ep.id, ep);
        }
    });

    // Testing linking events to their respective functions without large switch statement
    const eventKeyLinks = {
        'JOKI>:initialize': init,
    };
    eventKeyLinks[`${serviceId}:addEndpoint`] = addEndpoint;
    eventKeyLinks[`${serviceId}:endpointRequest`] = endpointRequest;
    eventKeyLinks[`${serviceId}:endpointLongRequest`] = endpointLongRequest;
    eventKeyLinks[`${serviceId}:endpointSend`] = endpointSend;
    eventKeyLinks[`${serviceId}:getGlobalFilters`] = () => Object.assign({}, globalFilters);

    /**
     * Main Event handler function registered to Joki
     * @param {*} event
     */
    function eventHandler(event) {
        const eventFullKey = `${event.from ? event.from + '>' : ''}${event.to || ''}:${event.key || ''}`;
        if (eventKeyLinks[eventFullKey]) {
            return eventKeyLinks[eventFullKey](event.body || null);
        }

        if (event.key === 'changeGlobalFilter' && (event.to === serviceId || event.from !== undefined)) {
            changeGlobalFilter(event.body);
        }
    }

    /**
     * Handle joki.initialize event
     * @param {*} body
     */
    function init(body) {
        // console.log("Initialize endpoints", body);
    }

    /**
     * Handle addEndpoint event
     * @param {*} endpointSetting
     */
    function addEndpoint(endpointSetting) { }

    function changeGlobalFilter(body) {
        const { key, value, type = 'enum' } = body;
        if (key !== undefined) {
            if (value !== undefined && value !== null) {
                if (type === 'string') {
                    globalFilters[key] = `"${value}"`;
                } else {
                    globalFilters[key] = value;
                }
            } else {
                delete globalFilters[key];
            }
        }

        // const cancelledHashes = endpointQueue.cancelAll();
        // console.log(endpointCache.keys());
        endpointCache.reset();

        // cancelledHashes.forEach(hashCode => {
        //     abortRequestByHashCode(hashCode);
        // });

        joki.broadcast({
            from: serviceId,
            key: 'DataResetRequired',
            body: {
                reason: 'Global filter value changed',
                filter: {
                    key: key,
                    value: value,
                },
            },
        });
    }

    /**
     * Handle endpointRequest Event
     *
     * The main function of this function is just validating the request is valid and that part should be skipped when in production
     * as these requests are hard coded into the source code.
     *
     * @param {*} eventBody
     */
    async function endpointRequest(eventBody = null) {
        // always make this check
        if (eventBody === null || typeof eventBody !== 'object') {
            throw new EndpointError(`ERROR:${serviceId}: No request defined in the event body or body does not contain a request object.`);
        }

        const { id, body = null, filters = {}, urlParse, contentType } = eventBody;
        // Do not run these checks in production to speed things up and to avoid throws
        if (!_PROD_) {
            if (typeof id !== 'string') {
                throw new EndpointError(`ERROR:${serviceId}: Endpoint id must be a string.`);
            }

            if (!endpoints.has(id)) {
                throw new EndpointError(`ERROR:${serviceId}: Unknown endpoint id '${id}'.`);
            }

            if (!services.has(endpoints.get(id).type)) {
                // console.warn("Endpoints", endpoints);
                // console.warn("services", services);
                throw new EndpointError(`ERROR:${serviceId}: Requested endpoint type '${endpoints.get(id).type}' is not configured.`);
            }
        }
        const endpoint = endpoints.get(id);
        const targetService = services.get(endpoint.type);
        try {
            const results = await requestFromService(targetService, endpoint, body, filters, urlParse, contentType);

            if (results === undefined) {
                return [undefined, 'ERR'];
            }
            return [results, undefined];
        } catch (err) {
            if (typeof err === 'string') {
                return [undefined, err];
            }
            return [undefined, 'ERR'];
        }
    }

    /**
     * Handle endpointLongRequest event
     */
    function endpointLongRequest() { }

    /**
     * Handle endpointSend event
     */
    function endpointSend() { }

    // function abortRequestByHashCode(hashCode) {
    //     const status = endpointCache.status(hashCode);
    //     if (status === 'FETCHING') {
    //         const extraData = endpointCache.data(hashCode);
    //         const endpoint = extraData.endpoint;
    //         if(endpoint.cancelKey) {
    //             const event = {
    //                 to: endpoint.serviceId,
    //                 key: endpoint.cancelKey,
    //                 body: hashCode,
    //             };
    //             joki.ask(event);
    //         }

    //         endpointCache.del(hashCode);
    //     }

    // }

    /**
     * Send a request to the service defined in the endpoint and service settings
     *
     * This needs to return a promise as we need more control over the execution flow than what simple async/await provides
     *
     * @param {*} service
     * @param {*} endpoint
     * @param {*} data
     * @param {*} requestFilters
     */
    function requestFromService(service, endpoint, data, requestFilters, urlParse, contentType) {
        return new Promise(async (resolve, reject) => {
            const event = {
                to: service.serviceId,
                from: serviceId,
                key: service.requestKey,
                body: parseEndpointData(endpoint, data, requestFilters, urlParse, contentType),
            };
            const hashCode = hash({ id: endpoint, data: event });
            // const hashCode = endpointQueue.getHash(endpoint, event);
            const cacheStatus = endpointCache.status(hashCode);

            if (cacheStatus === 'VALID') {
                resolve(endpointCache.get(hashCode));
            }

            try {
                if (cacheStatus === 'FETCHING') {
                    const results = await endpointCache.wait(hashCode);
                    resolve(results);
                }
            } catch (err) {
                if (err.errCode === 'DELETE') {
                    reject('DELETE');
                    return;
                }
                if (err.errCode === 'RESET') {
                    reject('RESET');
                    return;
                }
                reject(err);
                return;
            }

            try {
                endpointCache.begin(hashCode, false, { endpoint, data, filters: requestFilters, urlParse });
                event.body.abortId = hashCode;
                const jokiResponse = await joki.ask(event);

                if (jokiResponse[service.serviceId] === undefined) {
                    endpointCache.del(hashCode);
                }

                if (jokiResponse[service.serviceId].then !== undefined) {
                    jokiResponse[service.serviceId]
                        .then(results => {

                            const currentStatus = endpointCache.status(hashCode);
                            if (currentStatus !== 'FETCHING') {
                                reject('CANCEL');
                            } else {
                                if (results !== undefined) {
                                    endpointCache.end(hashCode, results, true);
                                    resolve(results);
                                } else {
                                    endpointCache.del(hashCode);
                                    console.warn(`${serviceId}:WARN:${hashCode}: Response was undefined for endpoint ${endpoint.id}`);
                                    reject(`${serviceId}:EndpointRequest:${endpoint.id}: Results were undefined`);
                                }
                            }
                        })
                        .catch(err => {
                            if (err.name !== 'undefined' && err.name === 'AbortError') {
                                endpointCache.del(hashCode);
                                // endpointQueue.cancel(hashCode);
                                // endpointQueue.done(hashCode);
                                reject(err);
                            } else {
                                console.error(`\t${serviceId};ERR`, err);
                                endpointCache.del(hashCode);
                                // endpointQueue.cancel(hashCode);
                                // endpointQueue.done(hashCode);
                                reject(err);
                            }
                        });
                }
            } catch (err) {
                endpointCache.del(hashCode);
            }
        });
    }

    /**
     * Parse the body according to the endpoint.
     *
     * @param {*} endpoint
     * @param {*} data
     * @param {*} requestFilters
     */
    function parseEndpointData(endpoint, data, requestFilters = {}, urlParse = {}, contentType = undefined) {
        const finalFilters = parseFilters(endpoint, requestFilters);
        switch (endpoint.type) {
            case ENDPOINTTYPE.GRAPHQL:
            case ENDPOINTTYPE.GRAPHQL_FORCED:
                return parseGraphQlEndpointData(endpoint, data, finalFilters, urlParse);
            case ENDPOINTTYPE.REST:
            case ENDPOINTTYPE.REST_CACHED:
                return parseRestEndpointData(endpoint, data, finalFilters, urlParse, contentType);
            case ENDPOINTTYPE.TEST:
                return parseTestEndpointData(endpoint, data, finalFilters, urlParse);
            default:
                throw new EndpointError(`No endpoint parses defined for ${endpoint.type}`);
        }
    }

    /**
     * Parse filters for the request
     */
    function parseFilters(endpoint, requestFilters) {
        const currentFilters = Object.assign({}, globalFilters, requestFilters);

        // If endpoint has no filters rules return current filters.
        if (!Array.isArray(endpoint.filters)) return currentFilters;

        // Handle endpoint filter settings
        const finalFilters = endpoint.filters.reduce((activeFilters, rule) => {
            if (typeof rule === 'string' && currentFilters[rule] !== undefined) {
                activeFilters[rule] = currentFilters[rule];
            }
            if (typeof rule === 'object' && rule !== null && typeof rule.key === 'string') {
                const { key, value, forced } = rule;
                if (forced === true) {
                    activeFilters[key] = value;
                } else {
                    if (currentFilters[key] === undefined) {
                        activeFilters[key] = value;
                    } else {
                        activeFilters[key] = currentFilters[key];
                    }
                }
            }
            return activeFilters;
        }, {});

        return finalFilters;
    }

    joki.addService({
        id: serviceId,
        fn: eventHandler,
    });
}

/**
 * Validate an array of endpoints
 * @param {*} endpoints
 * @param {*} throwOnErrors
 */
function endpointValidator(endpoints, throwOnErrors = false) {
    let errMsg = [];
    if (!Array.isArray(endpoints)) {
        errMsg.push('EndpointSettings must an an array of objects, where each object has settings for a single endpoint.');
    }

    const mandatoryKeys = ['id', 'type', 'url'];

    endpoints.forEach((endpoint, index) => {
        const missingKeys = objectHasKeys(endpoint, mandatoryKeys);
        if (missingKeys) {
            errMsg.push(
                `Mandatory keys for each endpoint are ${mandatoryKeys.join(
                    ', '
                )}. And endpoint at index ${index} is missing one or more keys: ${missingKeys.join(', ')}.`
            );
        }
        if (endpoint.type !== undefined && ENDPOINTTYPE[endpoint.type] === undefined) {
            errMsg.push(`Endpoint type ${endpoint.type} is not valid. Valid endpoint types are currently: ${Object.keys(ENDPOINTTYPE).join(', ')}.`);
        }
    });

    if (errMsg.length === 0) {
        return false;
    }

    if (throwOnErrors) {
        throw errMsg;
    } else {
        console.error(`Endpoint validation ERROR:\n ${errMsg.join('\n')} `);
    }
    return errMsg;
}

class EndpointError extends Error {
    constructor(message) {
        super(message);
        this.name = 'EndpointError';
    }
}

/**
 * Check that the object has keys.
 *
 * Returns an array of missing keys or undefined if all keys are present.
 *
 * @param {*} obj
 * @param {*} arrayOfKeys
 * @return {Array|undefined}
 */
function objectHasKeys(obj, arrayOfKeys) {
    const missingKeys = [];
    arrayOfKeys.forEach(key => {
        if (obj[key] === undefined) {
            missingKeys.push(key);
        }
    });
    if (missingKeys.length === 0) return;
    return missingKeys;
}

// /***********************************
//  * Handle endpoint queue cancellations etc.
//  *
//  * @param {*} opts
//  */
// export function endpointHistoryQueue(opts = {}) {
//     const queue = new Map();
//     const lifetimeInMs = opts.lifetime || 10000;

//     function addToQueue(endpoint, event) {
//         const endpointId = endpoint.id;
//         const hashCode = createHash(endpointId, event);
//         if (queue.has(hashCode)) {
//             // const req = queue.get(hashCode);

//             cleanObsoleteRequests();

//             if (queue.has(hashCode)) {
//                 const current = queue.get(hashCode);
//                 if (current.requesting) {
//                     throw new EndpointQueueError(
//                         `Identical request for ${endpointId} with hash ${hashCode} is still in progress.`,
//                         endpoint,
//                         'IdenticalEndpointRequestOnGoing'
//                     );
//                 }
//                 throw new EndpointQueueError(
//                     `Queue Hash ${hashCode} for endpoint ${endpointId} already exists`,
//                     endpoint,
//                     'DuplicateHash'
//                 );
//             }
//         }

//         queue.set(hashCode, {
//             id: endpointId,
//             hash: hashCode,
//             created: Date.now(),
//             requesting: true,
//             cancelled: false,
//             endpoint: endpoint,
//         });
//         return hashCode;
//     }

//     function requestDone(hashCode, data) {
//         if (queue.has(hashCode)) {
//             const req = queue.get(hashCode);
//             req.requesting = false;
//             req.doneAt = Date.now();
//             req.data = data;
//             req.duration = req.doneAt - req.created;
//             if (!req.endpoint.clearOnDone) {
//                 queue.set(hashCode, req);
//             } else {
//                 queue.delete(hashCode);
//                 return true;
//             }

//             if (!req.cancelled) return true;
//         }
//         return false;
//     }

//     function cancelHash(hashCode) {
//         if (queue.has(hashCode)) {
//             const req = queue.get(hashCode);
//             req.cancelled = true;
//             req.cancelledAt = Date.now();
//             queue.set(hashCode, req);
//         }
//     }

//     function cancelAllSpecificEndpointRequests(endpointId) {
//         const cancelledHashes = [];
//         queue.forEach((req, hashCode) => {
//             if (req.id === endpointId && !req.done && !req.cancelled) {
//                 cancelledHashes.push(hashCode);
//                 req.cancelled = true;
//                 req.cancelledAt = Date.now();
//                 queue.set(hashCode, req);
//             }
//         });
//         return cancelledHashes;
//     }

//     function cancelRequestingAndDeleteRest() {
//         const cancelledHashes = [];
//         queue.forEach((req, hashCode) => {
//             if (!req.cancelled) {
//                 req.cancelled = true;
//                 req.cancelledAt = Date.now();
//                 queue.set(hashCode, req);

//                 if (req.requesting) {
//                     cancelledHashes.push(hashCode);
//                 }
//             }
//             if (!req.requesting) {
//                 queue.delete(hashCode);
//             }
//         });
//         return cancelledHashes;
//     }

//     function getStatusForHashCode(hashCode) {
//         if (!queue.has(hashCode)) {
//             return undefined;
//         }

//         const req = queue.get(hashCode);

//         if (req.requesting) {
//             if (req.cancelled) {
//                 return 'CANCELLING';
//             }

//             if (_requestIsObsolete(req)) {
//                 return 'PENDING';
//             }

//             return 'REQUESTING';
//         }

//         if (!req.requesting && req.cancelled) {
//             return 'CANCELLED';
//         }

//         if (!req.requesting && !req.cancelled) {
//             if (_requestIsObsolete(req)) {
//                 return 'OBSOLETE';
//             }

//             return 'DONE';
//         }
//     }

//     function getCurrentQueue() {
//         return Array.from(queue);
//     }

//     function cleanObsoleteRequests() {
//         const obsoleteHashes = [];
//         queue.forEach((req, hashCode) => {
//             if (_requestIsObsolete(req) && !req.requesting) {
//                 obsoleteHashes.push(hashCode);
//             }
//         });

//         obsoleteHashes.forEach(hashCode => {
//             queue.delete(hashCode);
//         });
//     }

//     function createHash(endpointId, event) {
//         return hash({ id: endpointId, data: event });
//     }

//     function _requestIsObsolete(request) {
//         if (request.endpoint.clearOnDone) {
//             return true;
//         }
//         const lifetime = request.endpoint.lifetime
//             ? request.endpoint.lifetime
//             : lifetimeInMs;
//         return request.created + lifetime < Date.now();
//     }

//     function getInfoForHashCode(hashCode) {
//         if (queue.has(hashCode)) {
//             return { ...queue.get(hashCode) };
//         }
//         return undefined;
//     }

//     return {
//         add: addToQueue,
//         done: requestDone,

//         status: getStatusForHashCode,
//         info: getInfoForHashCode,
//         queue: getCurrentQueue,
//         getHash: createHash,

//         cancel: cancelHash,
//         cancelEndpoint: cancelAllSpecificEndpointRequests,
//         cancelAll: cancelRequestingAndDeleteRest,

//         clean: cleanObsoleteRequests,
//     };
// }

// class EndpointQueueError extends Error {
//     constructor(message, endpoint, errCode) {
//         super(message);
//         this.name = 'EndpointQueueError';
//         this.endpoint = endpoint;
//         this.code = errCode || 'GeneralQueueError';
//     }
// }
